import {
  bypassesSuitabilityChecks,
  frozenTotalsByProduct,
  isWrapperCompatibleWithSourceAccount,
  type LockedHoldingCap,
  lockedHoldingCap,
} from "@repo/shared";
import type {
  ExistingSnapshotPosition,
  ExistingSnapshotResponse,
  GoalPortfolioSwitchPair,
  LocalizedString,
} from "@repo/shared/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, TrendingUp } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import * as flowInstancesApi from "@/lib/api/flow-instances";
import { getMandateState } from "@/lib/api/flow-instances/mandate";
import * as portfoliosApi from "@/lib/api/flow-instances/portfolios";
import * as publicGoalsApi from "@/lib/api/public-flow/goals";
import * as publicPortfoliosApi from "@/lib/api/public-flow/portfolios";
import { usePublicSession } from "@/lib/context/public-session-context";
import { useInvalidateFlowCompletion } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { goalSnapshotQueryKeys } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import {
  goalQueryKeys,
  useGoal,
  useProductsWithSuitability,
  useSuitabilityContext,
} from "@/lib/hooks/use-flow-instances/use-goals";
import {
  invalidateGoalPortfolios,
  invalidatePortfolioFamily,
  portfolioQueryKeys,
  usePortfolio,
} from "@/lib/hooks/use-flow-instances/use-portfolios";
import { productAssessmentKeys } from "@/lib/hooks/use-flow-instances/use-product-assessment";
import { publicGoalQueryKeys } from "@/lib/hooks/use-public-flow";
import { publicPortfolioSnapshotQueryKeys } from "@/lib/hooks/use-public-flow/use-goal-snapshots";
import {
  invalidatePublicGoalPortfolios,
  invalidatePublicPortfolioFamily,
  publicPortfolioKeys,
} from "@/lib/hooks/use-public-flow/use-portfolios";
import { keys as publicProductAssessmentKeys } from "@/lib/hooks/use-public-flow/use-product-assessment";
import { publicFlowCompletionQueryKey } from "@/lib/hooks/use-public-flow/use-public-flow-completion";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import {
  checkProductsSuitability,
  type ProductSuitabilityResult,
  type ProductWithSuitabilityMeta,
  type SuitabilityContext,
} from "@/lib/services/suitability-service";
import type { RiskModelFilterOptions, SnapshotRefreshHints } from "@/lib/types/goal";
import { ApplyMandateDialog } from "@/pages/clients/detail/flows/instance/self-contained/mandate-widget/index";
import { formatAccountMinimumLines } from "../account-minimum-line";
import { AddProductDialog } from "./add-product-dialog";
import { T } from "./custom-portfolio-builder.i18n";
import type { CustomPortfolioBuilderProps, CustomProduct } from "./custom-portfolio-builder-types";
import { DroppedInstrumentsSection } from "./dropped-instruments-section";
import { PortfolioProductTable } from "./portfolio-product-table";
import { SellHoldingDialog } from "./sell-holding-dialog";
import { SwitchPairDialog, type SwitchSource } from "./switch-pair-dialog";
import { minBuyFollowupLineKey, useMinBuyFollowupLine } from "./use-min-buy-followup-line";

export function CustomPortfolioBuilder({
  goalId,
  portfolioId,
  style = "none",
  title,
  description,
  showIsin = false,
  showProductType = false,
  onlyAllowSuitableProducts = false,
  onSaveComplete,
  goals: propGoals,
  isCompleted: propIsCompleted,
  onSuitabilityBlockerChange,
  hasValidationError = false,
  missingReason,
}: CustomPortfolioBuilderProps) {
  const { isPublic, sessionToken } = usePublicSession();
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const { formatNumber } = useRegionalFormat();
  const t = useT(T);
  const minBuyFollowupLine = useMinBuyFollowupLine();

  // Both renderers pass isCompleted; default to editable.
  const isCompleted = propIsCompleted ?? false;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<CustomProduct | null>(null);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [amount, setAmount] = useState("");
  const [units, setUnits] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  // Unmatchable holding whose keep/sell PATCH is in flight (QL-203).
  const [togglingHoldingId, setTogglingHoldingId] = useState<string | null>(null);
  // QL-304 — unmatchable holding whose sell-amount dialog is open (null = closed).
  const [sellDialogHolding, setSellDialogHolding] = useState<ExistingSnapshotPosition | null>(null);
  // QL-283 — snapshot position whose switch-pair dialog is open (null = closed).
  // Stored as the id so the dialog always renders the freshest pairs from the
  // refetched portfolio payload after each mutation.
  const [switchDialogPositionId, setSwitchDialogPositionId] = useState<string | null>(null);
  const [switchSaving, setSwitchSaving] = useState(false);
  // QL-375 group 6 — screen 10: the mandate entry point of the empty state.
  const [applyMandateOpen, setApplyMandateOpen] = useState(false);

  // Goal still gates visibility (the goal must exist); the portfolio supplies type + products.
  const currentGoal = propGoals?.find((g) => g.id === goalId);

  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();

  // --- Advisor flow queries (enabled when not client flow) ---
  // QP-150 — custom products + portfolio type come from the portfolio; the goal still
  // provides the suitability/risk context and the parent flowInstanceId.
  const { goal: advisorGoal, isLoading: advisorGoalLoading } = useGoal(!isPublic ? goalId : null);
  const { portfolio: advisorPortfolio, isLoading: advisorPortfolioLoading } = usePortfolio(
    !isPublic ? portfolioId : null,
  );
  const {
    products: advisorProducts,
    isLoading: advisorProductsLoading,
    isSuccess: advisorProductsReady,
  } = useProductsWithSuitability(!isPublic ? goalId : null);
  const { context: advisorContext, isLoading: advisorContextLoading } = useSuitabilityContext(
    !isPublic ? goalId : null,
  );
  const { data: advisorRiskModelOptions, isLoading: advisorRiskModelLoading } = useQuery({
    queryKey: goalQueryKeys.riskModelOptions(!isPublic ? goalId : null),
    queryFn: () => flowInstancesApi.getRiskModelOptions(goalId),
    enabled: !isPublic && !!goalId,
  });

  // QL-375 group 6 — the goal's mandate state decides the empty state's second
  // button (screen 10): "Choose mandate template" while several templates
  // match and none is chosen, "Create from mandate" once the goal has a mandate.
  // Same key as the mandate panel, so one read serves both. Advisor flow only.
  const mandateInstanceId = !isPublic ? (advisorGoal?.flowInstanceId ?? null) : null;
  // QL-677 — the portfolio scope: it carries Apply's availability and the
  // templates the dialog may build from (the same key the portfolio page's
  // mandate panel reads, so one read serves both).
  const { data: mandateState } = useQuery({
    queryKey: [...goalSnapshotQueryKeys.mandate(goalId), "portfolio", portfolioId] as const,
    queryFn: () => getMandateState(mandateInstanceId!, goalId, "portfolio", portfolioId),
    enabled: !!mandateInstanceId,
  });

  // --- Public flow queries (enabled when client flow) ---
  // QP-150 — products + portfolio type come from the public portfolio; suitability/risk
  // context stays goal-keyed (the goal owns the risk assessment).
  const { data: publicPortfolio, isLoading: publicPortfolioLoading } = useQuery({
    queryKey: publicPortfolioKeys.portfolio(portfolioId),
    queryFn: () => publicPortfoliosApi.getPublicPortfolio(portfolioId, sessionToken!),
    enabled: isPublic,
  });
  const {
    data: publicProducts,
    isLoading: publicProductsLoading,
    isSuccess: publicProductsReady,
  } = useQuery({
    queryKey: publicGoalQueryKeys.productsSuitability(goalId),
    queryFn: () =>
      publicGoalsApi.getPublicProductsWithSuitability(goalId, sessionToken!, userLanguage),
    enabled: isPublic,
  });
  const { data: publicContext, isLoading: publicContextLoading } = useQuery({
    queryKey: publicGoalQueryKeys.suitabilityContext(goalId),
    queryFn: () => publicGoalsApi.getPublicSuitabilityContext(goalId, sessionToken!, userLanguage),
    enabled: isPublic,
  });
  const { data: publicRiskModelOptions, isLoading: publicRiskModelLoading } = useQuery({
    queryKey: publicGoalQueryKeys.riskModelOptions(goalId),
    queryFn: () => publicGoalsApi.getPublicRiskModelOptions(goalId, sessionToken!, userLanguage),
    enabled: isPublic,
  });

  // Merge data from whichever flow is active. Custom products + portfolio type come from the
  // portfolio (QP-150); the public portfolio source is wired in 009 (advisor path here).
  const portfolioData = isPublic ? publicPortfolio : advisorPortfolio;
  const portfolioType = portfolioData?.portfolioType;
  const sourceAccountId = (portfolioData as any)?.sourceAccountId ?? null;
  // D5-r: agreement follow-ups surface their frozen baseline here too — an
  // unknown line renders as a highlighted "will be removed" row (no keep/sell:
  // the replace-all drop is forced, not a choice).
  const isAgreementFollowup = !!((portfolioData as any)?.sourceSavingsPlanId ?? null);
  // Both follow-up kinds fetch the frozen baseline (it powers the dropped
  // section and the comparisons) — but only ACCOUNT follow-ups get the
  // stock-dimension table semantics: Existing/Change columns, the
  // sell-don't-delete lock, wrapper flags and the locked-product edit rules.
  // An agreement draft is a free replace-all composition: no deltas, every
  // line deletable (removal = the fund leaves the new agreement).
  const isAccountFollowup = !!sourceAccountId;
  const isFollowup = isAccountFollowup || isAgreementFollowup;
  const sourceAccountWrapperType: string | null =
    (portfolioData as any)?.sourceAccountWrapperType ?? null;

  // Fetch existing portfolio snapshot for followup comparison (both advisor and public flows).
  // QL-502 review #2 fix round 2 (R1) — NOT gated on `isFollowup` (i.e.
  // `isAccountFollowup`/`isAgreementFollowup`, both derived from
  // `portfolioData`'s current, mutable fields): an account-followup
  // portfolio's `sourceAccountId` is nulled by a public-API account deletion
  // (`ON DELETE SET NULL`) while its `existing_portfolio_snapshot` rows (and
  // the server's frozen cap) survive it, so gating the fetch on that stale
  // signal reproduced the exact bug this ticket fixes — the row looked
  // unheld and fell to `removeOnly` instead of keep/reduce. The endpoint is
  // cheap (scoped by portfolioId+tenant) and already tells the caller
  // `source`/empty positions for a portfolio with nothing frozen, so
  // the query has no `enabled` — `hasFrozenBaseline` below does the gating.
  const {
    data: existingSnapshot,
    isLoading: existingSnapshotLoading,
    isSuccess: existingSnapshotReady,
  } = useQuery<ExistingSnapshotResponse>({
    queryKey: ["existing-snapshot", portfolioId, isPublic ? "public" : "advisor"],
    queryFn: () =>
      isPublic
        ? publicPortfoliosApi.getPublicExistingSnapshot(portfolioId, sessionToken)
        : portfoliosApi.getExistingSnapshot(portfolioId),
  });
  // QL-502 review #2 — a true frozen baseline (keep/reduce allowed, the
  // server's cap is the existing amount) vs the agreement follow-up's
  // savings-plan fallback (never frozen, server caps at 0) is which TABLE the
  // read came from, not whether the portfolio still links to a source
  // account — `sourceAccountId` can be nulled by a public-API account
  // deletion while the snapshot rows (and the server's cap) survive it.
  const hasFrozenBaseline = existingSnapshot?.source === "existing_portfolio_snapshot";
  // productId → existing amount / frozen unit count (units-type products) —
  // drives the Existing/Change columns and the locked-product cap. Summed per
  // product exactly like the server's `getFrozenSnapshotTotals` (QL-502 review:
  // a legacy multi-lot freeze must cap at the total, not at its last lot).
  const { amounts: existingAmountMap, units: existingUnitsMap } = useMemo(
    () => frozenTotalsByProduct(existingSnapshot?.positions ?? []),
    [existingSnapshot],
  );
  // Holdings that didn't match a collection product (QL-203) — shown read-only
  // on the existing side; they can never be bought or edited.
  const unmatchableHoldings = useMemo(
    () => (existingSnapshot?.positions ?? []).filter((pos) => !pos.productId),
    [existingSnapshot],
  );
  const customProducts: CustomProduct[] = useMemo(
    () => portfolioData?.customProducts ?? [],
    [portfolioData],
  );
  // QL-283 — the portfolio's switch pairs, grouped by source position.
  const switchPairs: GoalPortfolioSwitchPair[] = useMemo(
    () => (portfolioData as any)?.switchPairs ?? [],
    [portfolioData],
  );
  const switchPairsBySource = useMemo(() => {
    const map = new Map<string, GoalPortfolioSwitchPair[]>();
    for (const pair of switchPairs) {
      map.set(pair.sourcePositionId, [...(map.get(pair.sourcePositionId) ?? []), pair]);
    }
    return map;
  }, [switchPairs]);
  const positionIdByProductId = useMemo(() => {
    const map = new Map<string, string>();
    for (const pos of existingSnapshot?.positions ?? []) {
      if (pos.productId) map.set(pos.productId, pos.id);
    }
    return map;
  }, [existingSnapshot]);
  const productsWithSuitability: ProductWithSuitabilityMeta[] =
    (isPublic ? publicProducts : advisorProducts) ?? [];
  // QL-502 review #1 — true only once the active side's suitability query has
  // actually resolved (not: still loading or persistently erroring).
  // `lockedByProductId` below must not treat a not-yet-known suitability as
  // "not purchasable", or a transient fetch failure locks and tells the
  // advisor to remove every holding.
  const suitabilityReady = isPublic ? publicProductsReady : advisorProductsReady;
  const suitabilityContext: SuitabilityContext | null =
    (isPublic ? publicContext : advisorContext) ?? null;
  const riskModelOptions: RiskModelFilterOptions | null =
    (isPublic ? publicRiskModelOptions : advisorRiskModelOptions) ?? null;

  // The existing-snapshot query joins the gate so a followup never flashes the
  // "no products" empty state before its frozen holdings have arrived (QL-303).
  // QL-502 review — only for a follow-up: the query now always runs (see R1
  // above), but a plain flow needs no snapshot to render, so its failures and
  // retries must not hold the spinner. The lock derivation below waits for
  // the snapshot on its own (`existingSnapshotReady`).
  const loading =
    (isFollowup && existingSnapshotLoading) ||
    (isPublic
      ? publicPortfolioLoading ||
        publicProductsLoading ||
        publicContextLoading ||
        publicRiskModelLoading
      : advisorGoalLoading ||
        advisorPortfolioLoading ||
        advisorProductsLoading ||
        advisorContextLoading ||
        advisorRiskModelLoading);

  const totalAmount = customProducts.reduce((sum, p) => sum + p.amount, 0);

  // The product being added/edited, and whether it is ordered by whole units.
  const activeProductId = editingProduct ? editingProduct.productId : selectedProductId;
  const selectedMeta = productsWithSuitability.find((p) => p.id === activeProductId);
  const isUnitsProduct =
    (editingProduct?.product.orderAmountType ?? selectedMeta?.orderAmountType) === "units";

  // Wrapper-aware followups: NEW instruments (no snapshot position) whose
  // wrapper preference list excludes the source account's wrapperType will
  // open a pending account of their FIRST preference at completion — flag
  // them per row. The shared isWrapperCompatibleWithSourceAccount predicate
  // is the rule (QL-283 decision 19); a null-wrapper account takes
  // everything, so the memo short-circuits to no badges.
  const newWrapperAccountByProductId = useMemo(() => {
    const map = new Map<string, string>();
    if (!isAccountFollowup || sourceAccountWrapperType === null) return map;
    for (const cp of customProducts) {
      if (existingAmountMap.has(cp.productId)) continue;
      const prefs =
        productsWithSuitability.find((p) => p.id === cp.productId)?.wrapperSyncKeys ?? [];
      if (prefs.length === 0) continue;
      if (isWrapperCompatibleWithSourceAccount(prefs, sourceAccountWrapperType)) continue;
      map.set(cp.productId, prefs[0]!);
    }
    return map;
  }, [
    isAccountFollowup,
    customProducts,
    existingAmountMap,
    productsWithSuitability,
    sourceAccountWrapperType,
  ]);

  // Calculate suitability for all products in the portfolio
  const suitabilityResults = useMemo((): Map<string, ProductSuitabilityResult> => {
    if (!suitabilityContext || productsWithSuitability.length === 0) {
      return new Map();
    }
    return checkProductsSuitability(productsWithSuitability, suitabilityContext, userLanguage);
  }, [productsWithSuitability, suitabilityContext, userLanguage]);

  // QL-502 — every row's lock state, derived once (see lockedHoldingCap).
  // Only a true frozen baseline (`hasFrozenBaseline`) is a keep/reduce cap;
  // an agreement follow-up's map entries are savings-plan lines the server
  // caps at 0. `purchasable` is `null` (never locks) until the active
  // suitability query actually succeeds — review #1.
  // The snapshot must have resolved too: until it has, `hasFrozenBaseline` is
  // unknown and a frozen held row would wrongly read as remove-only (the
  // builder no longer waits for the snapshot on a non-follow-up portfolio).
  // QL-707 — the server serves `suitability` only on a locked read of an advised
  // record, so its presence (not `isCompleted`, which a compliance reviewer's
  // read-only view of a still-editable draft also sets) says "this view is the
  // frozen record": nothing there is purchasable or removable, and the live
  // collection must not paint lock icons or the "not in the collection" banner
  // (a deleted product would otherwise grow both).
  const showsFrozenRecord = useMemo(
    () => customProducts.some((cp) => cp.suitability !== undefined),
    [customProducts],
  );
  const lockedByProductId = useMemo(() => {
    const map = new Map<string, LockedHoldingCap>();
    if (showsFrozenRecord || !suitabilityReady || !existingSnapshotReady) return map;
    const purchasableIds = new Set(productsWithSuitability.map((p) => p.id));
    for (const cp of customProducts) {
      const lock = lockedHoldingCap({
        purchasable: purchasableIds.has(cp.productId),
        hasFrozenBaseline,
        existingAmount: hasFrozenBaseline ? (existingAmountMap.get(cp.productId) ?? null) : null,
        existingUnits: hasFrozenBaseline ? (existingUnitsMap.get(cp.productId) ?? null) : null,
      });
      if (lock.locked) map.set(cp.productId, lock);
    }
    return map;
  }, [
    showsFrozenRecord,
    suitabilityReady,
    existingSnapshotReady,
    customProducts,
    productsWithSuitability,
    hasFrozenBaseline,
    existingAmountMap,
    existingUnitsMap,
  ]);
  const hasLockedHoldings = lockedByProductId.size > 0;
  // The banner names what the locked rows allow: keep/reduce for a frozen
  // baseline, removal otherwise — both sentences when both kinds are present.
  // Keyed off the lock state itself (the snapshot `source`), not the mutable
  // `isAccountFollowup`, which a public-API account deletion clears.
  const { hasFrozenLockedRow, hasRemoveOnlyLockedRow } = useMemo(() => {
    const locks = [...lockedByProductId.values()];
    return {
      hasFrozenLockedRow: locks.some((l) => l.locked && l.frozen),
      hasRemoveOnlyLockedRow: locks.some((l) => l.locked && !l.frozen),
    };
  }, [lockedByProductId]);

  // Check if current client bypasses suitability checks (Professional/Eligible Counterparty)
  const clientBypassesSuitability = useMemo(() => {
    return bypassesSuitabilityChecks(suitabilityContext?.classification ?? null);
  }, [suitabilityContext]);

  // Check if portfolio has unsuitable products (for blocking form submission)
  const hasUnsuitableProducts = useMemo(() => {
    if (clientBypassesSuitability) return false;
    if (customProducts.length === 0) return false;

    return customProducts.some((cp) => {
      const suitability = suitabilityResults.get(cp.productId);
      return suitability && !suitability.isSuitable;
    });
  }, [clientBypassesSuitability, customProducts, suitabilityResults]);

  // Update store with suitability blocker status
  useEffect(() => {
    if (onlyAllowSuitableProducts) {
      onSuitabilityBlockerChange?.(goalId, hasUnsuitableProducts);
    } else {
      onSuitabilityBlockerChange?.(goalId, false);
    }
  }, [goalId, onlyAllowSuitableProducts, hasUnsuitableProducts, onSuitabilityBlockerChange]);

  // Cleanup: remove blocker when component unmounts
  useEffect(() => {
    return () => {
      onSuitabilityBlockerChange?.(goalId, false);
    };
  }, [goalId, onSuitabilityBlockerChange]);

  // Get product type IDs that client has experience with
  const experiencedProductTypeIds = useMemo(() => {
    if (!suitabilityContext?.experience?.experiences) return new Set<string>();
    return new Set(
      suitabilityContext.experience.experiences
        .filter((exp) => exp.hasExperience && exp.productTypeId)
        .map((exp) => exp.productTypeId),
    );
  }, [suitabilityContext]);

  const handleAddProduct = () => {
    setEditingProduct(null);
    setSelectedProductId("");
    setAmount("");
    setUnits("");
    setDialogOpen(true);
  };

  const handleEditProduct = (product: CustomProduct) => {
    setEditingProduct(product);
    setSelectedProductId(product.productId);
    setAmount(product.amount.toString());
    setUnits(product.units != null ? String(product.units) : "");
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingProduct(null);
    setSelectedProductId("");
    setAmount("");
    setUnits("");
  };

  const handleSubmit = async () => {
    // Don't allow modifications on completed flows
    if (isCompleted) return;

    const amountNum = parseFloat(amount);
    const unitsNum = Number(units);

    // Full sell of a HELD account-followup instrument: the dialog's Sell
    // segment drove the target to zero — sanctioned through the same update
    // endpoint with the payload the old row-level sell-all sent (the
    // service's units branch only reads `units`, so units rows send both
    // zeros). Exempt from the nonzero/min-buy validations below.
    const fullSell =
      isAccountFollowup &&
      !!editingProduct &&
      existingAmountMap.has(editingProduct.productId) &&
      (isUnitsProduct ? units !== "" && unitsNum === 0 : amount !== "" && amountNum === 0);

    // QL-502 — never send a target above the server's purchasability cap
    // (the dialog disables Save first; this is the same backstop pattern as
    // the min-buy check below).
    const lock = editingProduct ? lockedByProductId.get(editingProduct.productId) : undefined;
    if (lock?.locked && !fullSell) {
      const target = isUnitsProduct ? unitsNum : amountNum;
      const cap = isUnitsProduct ? (lock.capUnits ?? 0) : lock.capAmount;
      if (target > cap) {
        toast.error(t.validationError, {
          description: lock.frozen ? t.notAvailableForPurchase : t.lockedRemoveOnly,
        });
        return;
      }
    }

    // Units products take a whole number of units; amount products take a value.
    if (fullSell) {
      // No further validation — target zero is always a legal instruction.
    } else if (isUnitsProduct) {
      if (!units || !Number.isInteger(unitsNum) || unitsNum < 1) {
        toast.error(t.validationError, { description: t.unitsMustBeWholeNumber });
        return;
      }
    } else if (!amount || amountNum <= 0) {
      toast.error(t.validationError, {
        description: t.amountMustBeGreaterThanZero,
      });
      return;
    } else {
      // QP-193 — a positive purchase below the product's minimum buy can't be
      // saved (mirrors the server-side guard in the goal-portfolio service).
      // QL-398 — on an account follow-up a held product's row is the TARGET;
      // the purchase is the top-up, and keep / sell (no top-up) are never judged.
      const minBuy = selectedMeta?.minBuyAmount ?? null;
      const existing =
        isAccountFollowup && editingProduct
          ? (existingAmountMap.get(editingProduct.productId) ?? null)
          : null;
      // Cents, like the server (`assertMinBuySatisfied`) and the dialog's own
      // round2: 65 536.18 − 15 536.18 is 49 999.999…, not 50 000, in floats.
      const purchase =
        existing != null ? Math.round((amountNum - existing) * 100) / 100 : amountNum;
      if (minBuy != null && minBuy > 0 && purchase > 0 && purchase < minBuy) {
        toast.error(t.validationError, {
          description:
            existing != null
              ? t.minBuyTopupError(formatNumber(purchase), formatNumber(minBuy))
              : t.minBuyDialogError(formatNumber(minBuy)),
        });
        return;
      }
    }

    if (!editingProduct && !selectedProductId) {
      toast.error(t.validationError, {
        description: t.pleaseSelectProduct,
      });
      return;
    }

    // Payload field that varies by order type (the rest is identical).
    const orderPayload = fullSell
      ? isUnitsProduct
        ? { amount: 0, units: 0 }
        : { amount: 0 }
      : isUnitsProduct
        ? { units: unitsNum }
        : { amount: amountNum };

    setIsSubmitting(true);

    try {
      let refreshHints: SnapshotRefreshHints | undefined;

      if (isPublic) {
        // Client/public flow — QP-150 products live on the portfolio.
        if (editingProduct) {
          const result = await publicPortfoliosApi.updatePublicPortfolioProduct(
            editingProduct.id,
            { ...orderPayload, lang: userLanguage },
            sessionToken,
          );
          refreshHints = result.refreshHints as SnapshotRefreshHints | undefined;
        } else {
          const result = await publicPortfoliosApi.addPublicPortfolioProduct(
            portfolioId,
            { productId: selectedProductId, ...orderPayload, weight: 0, lang: userLanguage },
            sessionToken,
          );
          refreshHints = result.refreshHints as SnapshotRefreshHints | undefined;
        }
      } else {
        // Advisor flow — QP-150 products live on the portfolio.
        if (editingProduct) {
          const result = await portfoliosApi.updatePortfolioProduct(
            editingProduct.id,
            orderPayload,
          );
          refreshHints = result.refreshHints as SnapshotRefreshHints | undefined;
        } else {
          const result = await portfoliosApi.addPortfolioProduct(portfolioId, {
            productId: selectedProductId,
            ...orderPayload,
            weight: 0,
          });
          refreshHints = result.refreshHints as SnapshotRefreshHints | undefined;
        }
      }

      handleDialogClose();
      invalidateAfterCompositionChange();

      if (onSaveComplete) {
        await onSaveComplete(refreshHints);
      }

      toast.success(editingProduct ? t.productUpdatedSuccessfully : t.productAddedSuccessfully);
    } catch (err) {
      toast.error(t.error, {
        description: err instanceof Error ? err.message : t.failedToSaveProduct,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Composition changed (product save / delete / sell-all) — the one
  // invalidation set every mutation of the proposed portfolio must fire.
  // Returns the PORTFOLIO detail refetch promise (the payload that carries
  // pairs/holdings): callers whose UI re-enables against that data await it;
  // everything else in the set stays fire-and-forget.
  const invalidateAfterCompositionChange = (): Promise<void> => {
    if (isPublic) {
      const portfolioRefetch = queryClient.invalidateQueries({
        queryKey: publicPortfolioKeys.portfolio(portfolioId),
      });
      // Analysis widgets (x-ray, allocation, forecast, cost…) — the server
      // regenerated the stored snapshots in the mutation request; without
      // this the widgets served the pre-mutation cache until the 60s
      // staleTime expired ("loads after a couple of tab switches",
      // 2026-09-15). Explicit even though the unified key families make it
      // a prefix-subset of the detail invalidation above — invalidations
      // must not depend on key-shape coincidences.
      queryClient.invalidateQueries({
        queryKey: publicPortfolioSnapshotQueryKeys.allForPortfolio(portfolioId),
      });
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
      // Products changed → the product-assessment "not suitable" warning depends on which
      // categories are in use. Refresh the in-use set.
      queryClient.invalidateQueries({ queryKey: publicProductAssessmentKeys.productTypesInUse });
      // QL-362 — account-minimum violations aggregate across the goal's
      // portfolios, so the siblings' payloads are stale too. The portfolio-input
      // widget reads them from this query (the page renderers' `portfolio` object).
      invalidatePublicGoalPortfolios(queryClient, goalId);
      invalidatePublicPortfolioFamily(queryClient);
      return portfolioRefetch;
    }
    const portfolioRefetch = queryClient.invalidateQueries({
      queryKey: portfolioQueryKeys.portfolio(portfolioId),
    });
    // QL-362 — see the public branch: siblings feeding the same account.
    invalidateGoalPortfolios(queryClient, goalId);
    invalidatePortfolioFamily(queryClient);
    if (advisorGoal?.flowInstanceId) {
      queryClient.invalidateQueries({
        queryKey: productAssessmentKeys.productTypesInUse(advisorGoal.flowInstanceId),
      });
      // Direct API calls (bypass the use*PortfolioProduct hooks) — invalidate completion inline.
      invalidateCompletion(advisorGoal.flowInstanceId);
    }
    return portfolioRefetch;
  };

  const handleDeleteRequest = (productId: string) => {
    setProductToDelete(productId);
    setConfirmDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!productToDelete) return;
    if (isCompleted) return;

    try {
      let refreshHints: SnapshotRefreshHints | undefined;

      if (isPublic) {
        // Client/public flow — QP-150 products live on the portfolio.
        const result = await publicPortfoliosApi.deletePublicPortfolioProduct(
          productToDelete,
          sessionToken,
        );
        refreshHints = result.refreshHints as SnapshotRefreshHints | undefined;
      } else {
        // Advisor flow — QP-150 products live on the portfolio.
        const result = await portfoliosApi.deletePortfolioProduct(productToDelete);
        refreshHints = result.refreshHints as SnapshotRefreshHints | undefined;
      }

      invalidateAfterCompositionChange();

      if (onSaveComplete) {
        await onSaveComplete(refreshHints);
      }

      toast.success(t.productDeletedSuccessfully);
    } catch (err) {
      toast.error(t.error, {
        description: err instanceof Error ? err.message : t.failedToDeleteProduct,
      });
    }
    setProductToDelete(null);
  };

  // QL-304/QL-283 — keep/sell decision for an unmatchable holding (null =
  // keep; a value sells that much, server-capped at the frozen amount).
  // Decision 18 (unified action parity): available on the client flow too,
  // through the public endpoint wrapping the same service.
  const handleSetSellAmount = async (positionId: string, sellAmount: number | null) => {
    if (isCompleted) return;
    setTogglingHoldingId(positionId);
    try {
      const result = isPublic
        ? await publicPortfoliosApi.updatePublicExistingHolding(
            portfolioId,
            positionId,
            { sellAmount, lang: userLanguage },
            sessionToken,
          )
        : await portfoliosApi.updateExistingHolding(portfolioId, positionId, { sellAmount });
      await queryClient.invalidateQueries({
        queryKey: ["existing-snapshot", portfolioId, isPublic ? "public" : "advisor"],
      });
      // The sell decision reshapes the proposed side (QL-304 surviving
      // holdings) — the server regenerated the stored snapshots and its
      // hints drive the same refresh path as every other composition
      // mutation; the invalidations then re-read the FRESH artifacts.
      invalidateAfterCompositionChange();
      if (onSaveComplete) {
        await onSaveComplete(result.refreshHints as SnapshotRefreshHints | undefined);
      }
      setSellDialogHolding(null);
    } catch (err) {
      toast.error(t.error, {
        description: err instanceof Error ? err.message : t.failedToUpdateHolding,
      });
    } finally {
      setTogglingHoldingId(null);
    }
  };

  // ─── Switch pairs (QL-283) ────────────────────────────────────────────────

  // The open dialog's source, derived live so it always shows the freshest
  // pairs after a mutation round-trip refreshed the portfolio payload.
  const switchDialogSource: SwitchSource | null = useMemo(() => {
    if (!switchDialogPositionId) return null;
    const pos = existingSnapshot?.positions.find((p) => p.id === switchDialogPositionId);
    if (!pos) return null;
    return {
      positionId: pos.id,
      displayName:
        (pos.productName && localize(pos.productName as LocalizedString)) ||
        pos.name ||
        pos.isin ||
        "—",
      amount: pos.amount,
      // Units mode only for matched units products (frozen units present).
      units: pos.productId != null ? (pos.units ?? null) : null,
      productId: pos.productId,
    };
  }, [switchDialogPositionId, existingSnapshot, localize]);

  // Eligible targets: suitability-listed collection products, amount-based
  // only (decision 16), wrapper-compatible with the source account (decision
  // 19 — the shared isWrapperCompatibleWithSourceAccount predicate: a switch
  // executes inside the source account, so the only question is whether that
  // account can hold the target), never the source's own product.
  // Already-targeted products are filtered inside the dialog (edit that pair
  // instead).
  const switchTargetOptions = useMemo(
    () =>
      productsWithSuitability
        .filter(
          (p) =>
            p.orderAmountType !== "units" &&
            p.id !== switchDialogSource?.productId &&
            isWrapperCompatibleWithSourceAccount(p.wrapperSyncKeys, sourceAccountWrapperType),
        )
        // QL-398 — the target's minimum buy, so the dialog can refuse a pair
        // whose estimate falls short before the server does.
        .map((p) => ({
          id: p.id,
          name: p.name,
          isin: p.isin,
          minBuyAmount: p.minBuyAmount ?? null,
        })),
    [productsWithSuitability, switchDialogSource?.productId, sourceAccountWrapperType],
  );

  const runSwitchMutation = async (
    fn: () => Promise<{ refreshHints?: unknown }>,
  ): Promise<void> => {
    setSwitchSaving(true);
    try {
      const result = await fn();
      // Pairs ride the portfolio payload — the composition invalidation set
      // refreshes them along with completion + risk bounds + snapshots.
      // AWAITED: the dialog re-enables on switchSaving, and re-enabling
      // against the pre-mutation pairs/headroom opens a double-submit window.
      await invalidateAfterCompositionChange();
      if (onSaveComplete) {
        await onSaveComplete(result.refreshHints as SnapshotRefreshHints | undefined);
      }
    } catch (err) {
      toast.error(t.error, {
        description: err instanceof Error ? err.message : t.failedToUpdateProduct,
      });
      // The server may have mutated state before failing (e.g. deleted pairs
      // but failed regenerating snapshots) — re-read what it actually kept.
      void invalidateAfterCompositionChange();
    } finally {
      setSwitchSaving(false);
    }
  };

  const handleAddSwitchPair = (targetProductId: string, value: number) => {
    if (isCompleted || !switchDialogSource) return;
    const unitsMode = switchDialogSource.units != null && switchDialogSource.productId != null;
    const data = {
      sourcePositionId: switchDialogSource.positionId,
      targetProductId,
      ...(unitsMode ? { switchUnits: value } : { switchPercent: value }),
    };
    void runSwitchMutation(() =>
      isPublic
        ? publicPortfoliosApi.addPublicSwitchPair(portfolioId, data, sessionToken)
        : portfoliosApi.addSwitchPair(portfolioId, data),
    );
  };

  const handleUpdateSwitchPair = (pairId: string, value: number) => {
    if (isCompleted || !switchDialogSource) return;
    const unitsMode = switchDialogSource.units != null && switchDialogSource.productId != null;
    const data = unitsMode ? { switchUnits: value } : { switchPercent: value };
    void runSwitchMutation(() =>
      isPublic
        ? publicPortfoliosApi.updatePublicSwitchPair(portfolioId, pairId, data, sessionToken)
        : portfoliosApi.updateSwitchPair(portfolioId, pairId, data),
    );
  };

  const handleRemoveSwitchPair = (pairId: string) => {
    if (isCompleted) return;
    void runSwitchMutation(() =>
      isPublic
        ? publicPortfoliosApi.deletePublicSwitchPair(portfolioId, pairId, sessionToken)
        : portfoliosApi.deleteSwitchPair(portfolioId, pairId),
    );
  };

  // QL-283 — "Clear switch": remove every pair on the source holding in ONE
  // request; the server deletes them in one statement and regenerates the
  // snapshots once.
  const handleClearSwitchPairs = (sourcePositionId: string) => {
    if (isCompleted) return;
    void runSwitchMutation(() =>
      isPublic
        ? publicPortfoliosApi.clearPublicSwitchPairs(portfolioId, sourcePositionId, sessionToken)
        : portfoliosApi.clearSwitchPairs(portfolioId, sourcePositionId),
    );
  };

  // Don't render until goal is in store
  if (!currentGoal) {
    return null;
  }

  // Only show if portfolio type is 'custom'
  if (portfolioType !== "custom") {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-sm text-muted-foreground">{t.loadingCustomPortfolio}</div>
        </CardContent>
      </Card>
    );
  }

  // Server-computed min-buy breaches (QP-193) — empty unless the collection enforces them.
  const minBuyViolations = portfolioData?.minBuyViolations ?? [];
  // QL-398 — account follow-up: one entry per transaction, worded by kind. A
  // backstop only (the dialogs and the server refuse these at save), so this
  // is normally empty — it lists data that predates a raised minimum.
  const minBuyFollowupLines = minBuyViolations
    .filter((v) => v.followup)
    .map((v) => ({
      key: minBuyFollowupLineKey(v),
      text: minBuyFollowupLine(v),
    }));
  const minBuyMessage = isAccountFollowup
    ? t.minBuyFollowupViolationsMessage
    : t.minBuyViolationsMessage;
  const violatingPairIds = new Set(
    minBuyViolations.map((v) => v.followup?.switchPairId).filter((id): id is string => !!id),
  );

  // QL-362 — accounts this portfolio's one-time buys would leave below their
  // wrapper type's minimum. A goal-level aggregate: sibling portfolios feeding
  // the same account carry the same entry, so the fix may live on any of them.
  // Shown live (not gated on `hasValidationError`) so the advisor can react
  // while building rather than at submit.
  const accountMinimumViolations = portfolioData?.accountMinimumViolations ?? [];
  const accountMinimumLines = formatAccountMinimumLines(
    accountMinimumViolations,
    t.accountMinimumItem,
    localize,
    formatNumber,
  );

  /**
   * QL-138 — the component's validation line names the cause the engine
   * reported, so the message is derived from the reason it carries rather
   * than by re-deriving the gate order here. Risk bounds are not a cause any
   * more (QL-493): a breach is reported by the goal-level charts.
   */
  const validationMessage = (): string => {
    if (!hasValidationError) return "";
    if (missingReason === "min_buy_violation") return minBuyMessage;
    // QL-362 — the detailed per-account lines render right below this one.
    if (missingReason === "account_below_minimum") return t.accountMinimumBlockedMessage;
    // QL-283 — switch-pair backstop reasons (normally unreachable: the pair
    // service enforces both invariants at write time).
    if (missingReason === "switch_over_allocated") return t.switchOverAllocatedMessage;
    if (missingReason === "switch_regime_conflict") return t.switchRegimeConflictMessage;
    if (missingReason === "switch_wrapper_incompatible") return t.switchWrapperIncompatibleMessage;
    if (missingReason === "no_products") return t.incompleteMessage;
    // No reason on the entry — keep the pre-QL-138 behaviour.
    return minBuyViolations.length > 0 ? minBuyMessage : t.incompleteMessage;
  };
  const errorMessage = validationMessage();

  // Build container class based on style
  const getContainerClass = () => {
    if (hasValidationError) {
      return "border border-destructive rounded-lg p-4";
    }
    switch (style) {
      case "border":
        return "border rounded-lg p-4";
      case "box":
        return "border rounded-lg p-4 bg-muted/30";
      default:
        return "";
    }
  };

  // Product IDs already in the portfolio (to exclude from picker)
  const excludeProductIds = customProducts.map((cp) => cp.productId);

  // Screen 10 — which mandate button the empty state offers, if any: only a
  // non-follow-up custom portfolio on an editable instance, with the
  // collection's mandates on: Apply once the goal has a mandate (selected in
  // the Mandate component's selection section — QL-677 removed the picker
  // from here — or synced from the bank). The server enforces the same
  // conditions.
  // QL-677 — Apply no longer needs the goal's mandate: it builds from a
  // template picked in the dialog. Offered on every editable custom
  // portfolio; disabled with a hint when the platform has no template.
  const applyAvail = mandateState?.applyFromMandate;
  const mandateEntry: "apply" | null =
    !mandateInstanceId || !mandateState?.enabled || !mandateState.editable || isFollowup
      ? null
      : applyAvail && (applyAvail.available || applyAvail.disabledReason === "no_templates")
        ? "apply"
        : null;
  // Template mode needs something to pick: a goal whose mandate is a custom
  // one may be "available" with no template on the platform. QL-695 — a goal
  // using the bank's mandate always has the "Existing mandate" entry to build from.
  const applyHasExisting = mandateState?.mandate?.source === "bank";
  const applyDisabled =
    !!applyAvail &&
    (!applyAvail.available || (applyAvail.templates.length === 0 && !applyHasExisting));

  // Empty state — only when there is nothing to show on EITHER side. A
  // followup whose frozen holdings all fell outside the collection has no
  // custom products yet, but its existing side is not empty: the unmatchable
  // holdings (and an agreement's dropped lines) must render from the start,
  // not first appear once a collection product is added (QL-303).
  if (customProducts.length === 0 && !(isFollowup && unmatchableHoldings.length > 0)) {
    return (
      <>
        <div className={getContainerClass()}>
          <FlowComponentHeader title={title} description={description} className="mb-4" />
          {errorMessage && <p className="text-sm text-destructive mb-4">{errorMessage}</p>}
          {accountMinimumLines.map((line) => (
            <p key={line.key} className="text-sm text-destructive mb-4">
              {line.text}
            </p>
          ))}
          <div className="flex flex-col items-center justify-center py-12">
            <TrendingUp className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t.noProductsAddedYet}</h3>
            <p className="text-muted-foreground text-center mb-4 max-w-sm">
              {isCompleted
                ? t.noProductsWereAdded
                : mandateEntry === "apply"
                  ? t.buildYourCustomPortfolioOrMandate
                  : t.buildYourCustomPortfolio}
            </p>
            {!isCompleted && (
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button onClick={handleAddProduct}>{t.addFirstProduct}</Button>
                {mandateEntry === "apply" && (
                  <Button
                    variant="outline"
                    onClick={() => setApplyMandateOpen(true)}
                    disabled={applyDisabled}
                  >
                    {t.applyMandate}
                  </Button>
                )}
              </div>
            )}
            {!isCompleted && mandateEntry === "apply" && applyDisabled && (
              // A disabled button shows no title: the reason is written out (PR 688 review).
              <p className="mt-2 text-xs text-muted-foreground">{t.applyMandateNoTemplates}</p>
            )}
          </div>
        </div>

        {mandateInstanceId && mandateEntry === "apply" && (
          <ApplyMandateDialog
            open={applyMandateOpen}
            onOpenChange={setApplyMandateOpen}
            instanceId={mandateInstanceId}
            goalId={goalId}
            portfolioId={portfolioId}
            // QL-677 — "Create portfolio from mandate template": any platform
            // template, the goal's selected one preselected (else none); never
            // sets the mandate.
            // QL-695 — while the goal uses the bank's mandate, "Existing mandate:
            // {name}" leads the list and builds from it (the dialog reads `mandate`).
            mode="template"
            templates={applyAvail?.templates}
            initialTemplateId={mandateState?.selection.templateId ?? null}
            // QL-760 — the goal's risk-score matches lead the list.
            riskMatchIds={mandateState?.templateCandidates.map((c) => c.id)}
            mandate={mandateState?.mandate ?? null}
            perDeposit={portfolioData?.scheduleType === "recurring"}
            frequency={
              portfolioData?.scheduleType === "recurring"
                ? (portfolioData?.recurringFrequency ?? "monthly")
                : null
            }
            onApplied={() => {
              // Apply wrote products: the same refresh set as any composition
              // change, incl. the product-assessment in-use set (PR 598 review).
              invalidateAfterCompositionChange();
              invalidateCompletion(mandateInstanceId);
              onSaveComplete?.();
            }}
          />
        )}

        <AddProductDialog
          open={dialogOpen}
          onOpenChange={(next) => (next ? setDialogOpen(true) : handleDialogClose())}
          editingProduct={editingProduct}
          selectedProductId={selectedProductId}
          onSelectProduct={setSelectedProductId}
          amount={amount}
          onAmountChange={setAmount}
          isUnitsProduct={isUnitsProduct}
          units={units}
          onUnitsChange={setUnits}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          productsWithSuitability={productsWithSuitability}
          excludeProductIds={excludeProductIds}
          suitabilityResults={suitabilityResults}
          suitabilityContext={suitabilityContext}
          clientBypassesSuitability={clientBypassesSuitability}
          riskModelOptions={riskModelOptions}
          experiencedProductTypeIds={experiencedProductTypeIds}
          totalAmount={totalAmount}
        />
      </>
    );
  }

  return (
    <>
      <div className={`space-y-4 ${getContainerClass()}`}>
        <FlowComponentHeader title={title} description={description} className="mb-4" />
        {/* QL-138 — one line, named by the engine's reason (see `validationMessage`). */}
        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
        {/* A min-buy breach is worth showing even when the component is not (yet)
            flagged — the amounts are wrong regardless of whether validation ran. */}
        {!errorMessage && minBuyViolations.length > 0 && (
          <p className="text-sm text-destructive">{minBuyMessage}</p>
        )}
        {/* QL-398 — one line per follow-up transaction below its minimum, live
            from the portfolio payload (the only place a switch-only target is
            named as a fund). */}
        {minBuyFollowupLines.map((line) => (
          <p key={line.key} className="text-sm text-destructive">
            {line.text}
          </p>
        ))}
        {/* QL-362 — one line per breaching account, live from the portfolio payload. */}
        {accountMinimumLines.map((line) => (
          <p key={line.key} className="text-sm text-destructive">
            {line.text}
          </p>
        ))}

        {!isCompleted && (
          <div className="flex justify-end">
            <Button onClick={handleAddProduct} size="sm">
              {t.addProduct}
            </Button>
          </div>
        )}

        {/* QL-502 — at least one row is locked (product no longer purchasable):
            say so before the advisor opens a dialog or gets a 400 at save.
            Review #4 — hidden on a completed (read-only) instance: every
            action the banner's directive wording points at is already
            hidden there, so the text would just be noise. Review nit #8 —
            the "kept or reduced" wording only fires when a locked row
            actually has a frozen baseline to keep/reduce. */}
        {!isCompleted && hasLockedHoldings && (
          <Alert variant="warning">
            <Lock className="h-4 w-4" />
            <AlertDescription>
              {hasFrozenLockedRow && hasRemoveOnlyLockedRow
                ? t.lockedHoldingsBannerMixed
                : hasFrozenLockedRow
                  ? t.lockedHoldingsBannerFollowup
                  : t.lockedHoldingsBannerRemove}
            </AlertDescription>
          </Alert>
        )}

        {/* Both followup lineages: a seeded product deactivated/removed from
            the collection mid-draft loses its analytics — tell the advisor
            why. (The agreement path's amber section covers frozen lines that
            never matched, not draft rows whose product died after seeding.) */}
        {!showsFrozenRecord &&
          (isAccountFollowup || isAgreementFollowup) &&
          customProducts.some(
            (cp) => !productsWithSuitability.some((p) => p.id === cp.productId),
          ) && (
            <div className="text-sm text-muted-foreground bg-muted/50 border rounded-md p-3">
              {t.followupDisclaimer}
            </div>
          )}

        {isAgreementFollowup && <DroppedInstrumentsSection holdings={unmatchableHoldings} />}
        <PortfolioProductTable
          userLanguage={userLanguage}
          customProducts={customProducts}
          productsWithSuitability={productsWithSuitability}
          suitabilityResults={suitabilityResults}
          suitabilityContext={suitabilityContext}
          clientBypassesSuitability={clientBypassesSuitability}
          showIsin={showIsin}
          showProductType={showProductType}
          isCompleted={isCompleted}
          minBuyViolations={minBuyViolations}
          onEditProduct={handleEditProduct}
          onDeleteProduct={handleDeleteRequest}
          isFollowup={isAccountFollowup}
          existingAmountMap={existingAmountMap}
          existingUnitsMap={existingUnitsMap}
          lockedByProductId={lockedByProductId}
          unmatchableHoldings={isAgreementFollowup ? [] : unmatchableHoldings}
          newWrapperAccountByProductId={newWrapperAccountByProductId}
          // QL-283 decision 18 — unified action parity: the sell decisions are
          // available on the client flow too (public endpoint, same service).
          onEditHoldingSell={
            !isCompleted && !isAgreementFollowup ? setSellDialogHolding : undefined
          }
          busyHoldingId={togglingHoldingId}
          switchPairsBySource={switchPairsBySource}
          positionIdByProductId={positionIdByProductId}
          onOpenSwitch={
            !isCompleted && isAccountFollowup
              ? (source) => !source.blocked && setSwitchDialogPositionId(source.positionId)
              : undefined
          }
          // Completed instances keep the advised pairs inspectable — same
          // dialog, read-only (advice only; execution lives on the orders).
          onViewSwitch={
            isCompleted && isAccountFollowup
              ? (positionId) => setSwitchDialogPositionId(positionId)
              : undefined
          }
        />
        <SellHoldingDialog
          holding={sellDialogHolding}
          saving={togglingHoldingId !== null}
          onClose={() => setSellDialogHolding(null)}
          onSave={(sellAmount) =>
            sellDialogHolding && handleSetSellAmount(sellDialogHolding.id, sellAmount)
          }
        />
        <SwitchPairDialog
          source={switchDialogSource}
          pairs={
            switchDialogSource ? (switchPairsBySource.get(switchDialogSource.positionId) ?? []) : []
          }
          targetOptions={switchTargetOptions}
          violatingPairIds={violatingPairIds}
          saving={switchSaving}
          readOnly={isCompleted}
          onClose={() => setSwitchDialogPositionId(null)}
          onClearAll={() =>
            switchDialogSource && handleClearSwitchPairs(switchDialogSource.positionId)
          }
          onAddPair={handleAddSwitchPair}
          onUpdatePair={handleUpdateSwitchPair}
          onRemovePair={handleRemoveSwitchPair}
        />
      </div>

      <AddProductDialog
        open={dialogOpen}
        onOpenChange={(next) => (next ? setDialogOpen(true) : handleDialogClose())}
        editingProduct={editingProduct}
        selectedProductId={selectedProductId}
        onSelectProduct={setSelectedProductId}
        amount={amount}
        onAmountChange={setAmount}
        isUnitsProduct={isUnitsProduct}
        units={units}
        onUnitsChange={setUnits}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        productsWithSuitability={productsWithSuitability}
        excludeProductIds={excludeProductIds}
        suitabilityResults={suitabilityResults}
        suitabilityContext={suitabilityContext}
        clientBypassesSuitability={clientBypassesSuitability}
        riskModelOptions={riskModelOptions}
        experiencedProductTypeIds={experiencedProductTypeIds}
        totalAmount={totalAmount}
        isFollowup={isAccountFollowup}
        existingAmount={
          editingProduct ? (existingAmountMap.get(editingProduct.productId) ?? null) : null
        }
        existingUnits={
          editingProduct ? (existingUnitsMap.get(editingProduct.productId) ?? null) : null
        }
        lockedHolding={
          editingProduct ? (lockedByProductId.get(editingProduct.productId) ?? null) : null
        }
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteProduct}
        description={t.deleteProductConfirmation}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />
    </>
  );
}
